import Autocomplete, {
    AutocompleteRenderInputParams,
    AutocompleteValue,
    AutocompleteProps as MuiAutocompleteProps
} from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import { HTMLAttributes, Ref, SyntheticEvent, useCallback, useMemo } from 'react';
import { TextField, ITextFieldProps } from '../TextField';

export type ISelectOption = {
    id: string;
    label: string;
    custom?: { [key: string]: unknown };
};
type IProps<Multiple extends boolean | undefined, T = string> = MuiAutocompleteProps<T, Multiple, false, false>;
export type ISelectProps<Multiple extends boolean | undefined> = Omit<
    IProps<Multiple, ISelectOption>,
    'defaultValue' | 'getOptionLabel' | 'getOptionId' | 'name' | 'renderInput' | 'renderOption' | 'value' | 'onChange'
> &
    Pick<
        ITextFieldProps,
        'disabled' | 'enableDOMAttributeRequired' | 'label' | 'name' | 'placeholder' | 'readOnly' | 'required'
    > & {
        clearable?: boolean;
        disabledInput?: boolean;
        error?: boolean;
        helperText?: string;
        innerRef?: Ref<HTMLDivElement>;
        value?: IProps<Multiple>['value'];
        onChange?: (value: IProps<Multiple>['value']) => void;
    };

const SelectUnwrapped = <Multiple extends boolean | undefined>({
    disabled,
    disabledInput,
    disableCloseOnSelect = false,
    enableDOMAttributeRequired,
    error,
    helperText,
    innerRef,
    label,
    name,
    options,
    placeholder,
    readOnly,
    required,
    value,
    onChange,
    ...rest
}: ISelectProps<Multiple>) => {
    const handleChange = useCallback(
        (_: SyntheticEvent, selected: AutocompleteValue<ISelectOption, Multiple, false, false>) => {
            if (onChange) {
                onChange(
                    (Array.isArray(selected)
                        ? selected.map(({ id }) => id)
                        : (selected as ISelectOption | undefined)?.id ?? null) as IProps<Multiple>['value']
                );
            }
        },
        [onChange]
    );
    const checkIsOptionEqual = useCallback(
        (option: ISelectOption, selected: ISelectOption | ISelectOption[]) =>
            Array.isArray(selected) ? selected.some((item) => item.id === option.id) : option.id === selected.id,
        []
    );
    const getOptionLabel = useCallback((option: ISelectOption) => option.label, []);
    const renderOption = useCallback(
        (renderProps: HTMLAttributes<HTMLLIElement>, option: ISelectOption) => (
            <Box component="li" data-testid="select-item" {...renderProps}>
                {option.label}
            </Box>
        ),
        []
    );
    const renderInput = useCallback(
        (params: AutocompleteRenderInputParams) => (
            <TextField
                {...params}
                ref={innerRef}
                name={name}
                data-testid={`select_${name}`}
                placeholder={placeholder}
                label={label}
                disabled={disabledInput || disabled}
                enableDOMAttributeRequired={enableDOMAttributeRequired}
                required={required}
                error={error}
                helperText={helperText}
                readOnly={readOnly}
                InputLabelProps={{
                    ...params.InputLabelProps,
                    shrink: typeof placeholder === 'string' ? !!placeholder : undefined
                }}
            />
        ),
        [
            disabled,
            disabledInput,
            enableDOMAttributeRequired,
            error,
            helperText,
            innerRef,
            label,
            name,
            placeholder,
            readOnly,
            required
        ]
    );
    const cachedValue = useMemo(
        () =>
            (Array.isArray(value)
                ? options.filter(({ id }) => value.includes(`${id}`))
                : options.find(({ id }) => value === `${id}`) ?? null) as IProps<Multiple, ISelectOption>['value'],
        [options, value]
    );

    return (
        <Autocomplete<ISelectOption, Multiple>
            {...rest}
            autoHighlight
            disableCloseOnSelect={disableCloseOnSelect}
            isOptionEqualToValue={checkIsOptionEqual}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            disabled={disabled}
            options={options}
            readOnly={readOnly}
            renderInput={renderInput}
            value={cachedValue}
            onChange={handleChange}
        />
    );
};

export default SelectUnwrapped;
